import { useMutation, useQuery } from '@apollo/client';
import { Box, Button, Container, Paper, Typography, styled } from '@mui/material';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';
import { PasswordStrengthMeterBox } from '~/components/PasswordStrengthMeterBox';
import { PasswordField } from '~/components/forms/password-field';
import { ButtonLink } from '~/components/link';
import { AppContextDocument } from '~/contexts';
import { ResetPasswordDocument } from '~/generated/graphql';
import logo from '~/images/fc-logo.svg';
import { MIN_PASSWORD_STRENGTH, usePasswordStrength } from '~/lib/password';

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  backgroundColor: theme.palette.primary.main,
}));

const FormPaper = styled(Paper)(({ theme }) => ({
  margin: theme.spacing(0, 2),
  padding: theme.spacing(6),
  border: '1px solid #E6E6E6',
  borderRadius: '2px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

const Logo = styled('div')(({ theme }) => ({
  display: 'flex',
  flex: '1 1 40%',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-end',
  width: '75%',
  padding: theme.spacing(0, 0, 1),
}));

const messageSx = {
  textTransform: 'uppercase' as const,
  color: 'text.primary',
  px: 0,
  pt: 0,
  pb: 2,
};

const Form = styled('form')(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(1),
  textAlign: 'center',
}));

const FormInput = styled('div')(({ theme }) => ({
  margin: theme.spacing(1.5, 0),
}));

export const ResetPassword = () => {
  const { t } = useTranslation();

  const { token = '' } = useParams<{ token: string }>();

  // TODO: 404 if the params are bad.
  const { data } = useQuery(AppContextDocument, {
    fetchPolicy: 'cache-only',
  });

  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');

  const [resetPassword, resetState] = useMutation(ResetPasswordDocument, {
    fetchPolicy: 'no-cache',
  });

  const passwordStrength = usePasswordStrength(password);

  if (data?.currentUser != null) return <Navigate replace to="/" />;

  const success = resetState.data?.resetPassword?.success ?? false;

  return (
    <Root>
      <Helmet title={t('pages.auth.reset_password.title')} />
      <Container maxWidth="sm">
        <FormPaper elevation={5}>
          <Logo>
            <img alt="FanConnect" src={logo} />
          </Logo>
          <Typography component="h1" variant="h5" sx={messageSx}>
            {t('pages.auth.reset_password.title')}
          </Typography>
          {resetState.error && (
            <Typography align="center" color="error" role="alert">
              {t('pages.auth.reset_password.failed', { message: resetState.error.message })}
            </Typography>
          )}
          {confirmation !== '' && confirmation !== password && (
            <Typography align="center" color="error" role="status">
              {t('pages.auth.reset_password.passwords_no_match')}
            </Typography>
          )}
          {!resetState.called || resetState.loading ? (
            <Form
              onSubmit={async (event) => {
                event.preventDefault();
                await resetPassword({ variables: { password, token } }).catch(() => {
                  // TODO: Do something appropriate here
                });
              }}
            >
              <FormInput>
                <PasswordField
                  kind="new"
                  label={t('pages.auth.reset_password.new_password')}
                  fullWidth
                  value={password}
                  disabled={resetState.loading}
                  onChange={(event) => {
                    setPassword(event.target.value);
                  }}
                />
              </FormInput>
              <PasswordStrengthMeterBox strength={passwordStrength} />
              <FormInput>
                <PasswordField
                  kind="new"
                  label={t('pages.auth.reset_password.confirm_password')}
                  fullWidth
                  value={confirmation}
                  disabled={resetState.loading}
                  onChange={(event) => {
                    setConfirmation(event.target.value);
                  }}
                />
              </FormInput>
              <FormInput>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  color="secondary"
                  size="large"
                  disabled={
                    !password ||
                    !confirmation ||
                    (passwordStrength?.score ?? 0) < MIN_PASSWORD_STRENGTH ||
                    password !== confirmation ||
                    resetState.loading
                  }
                >
                  {t('pages.auth.reset_password.set_password')}
                </Button>
              </FormInput>
            </Form>
          ) : success ? (
            <>
              <Typography variant="subtitle1" textAlign="center" marginY="8px">
                {t('pages.auth.reset_password.success')}
              </Typography>
              <FormInput>
                <ButtonLink variant="contained" color="secondary" size="large" to="/login">
                  {t('pages.auth.reset_password.sign_in')}
                </ButtonLink>
              </FormInput>
            </>
          ) : (
            <>
              <Typography textAlign="center" marginY="8px" color="error" role="alert">
                {t('pages.auth.reset_password.error')}
              </Typography>
              <FormInput>
                <Button
                  variant="contained"
                  fullWidth
                  color="secondary"
                  size="large"
                  onClick={() => {
                    window.location.reload();
                  }}
                >
                  {t('pages.auth.reset_password.reload_page')}
                </Button>
              </FormInput>
            </>
          )}
        </FormPaper>
        <Box flex="1 1 40%" />
      </Container>
    </Root>
  );
};
